import Parameter from './Parameter';
import { InflationModel } from './InflationModel';
import {
  CapturedData,
  TrialResult,
  YearData,
  PersonState,
  PersonYearResult,
  InstantiatedGeneralParams,
  InstantiatedPersonParams,
  InstantiatedCareerParams,
  ActiveCareer
} from '../types';

// Simulation constants
const NUM_TRIALS = 1000;
const SIMULATION_YEARS = 70;

/**
 * Main simulation engine class
 */
export class SimulationEngine {
  private abortController: AbortController | null = null;

  /**
   * Run the complete Monte Carlo simulation
   */
  async runSimulation(
    capturedData: CapturedData,
    onTrialComplete?: (result: TrialResult, completed: number, total: number) => void,
    abortSignal?: AbortSignal
  ): Promise<TrialResult[]> {
    console.log('Starting simulation...');
    
    if (!this.validateCapturedData(capturedData)) {
      throw new Error('Invalid simulation data provided');
    }

    const totalTrials = (capturedData.general['num-trials'] as any)?.value ?? NUM_TRIALS;
    const allTrialResults: TrialResult[] = [];
    let completedTrials = 0;

    return new Promise((resolve, reject) => {
      const runTrials = () => {
        const batchSize = 50;
        
        try {
          for (let i = 0; i < batchSize && completedTrials < totalTrials; i++) {
            if (abortSignal?.aborted) {
              console.log('Simulation aborted');
              reject(new DOMException('Simulation aborted by user', 'AbortError'));
              return;
            }
            
            const trialResult = this.runSingleTrial(capturedData, abortSignal);
            allTrialResults.push(trialResult);
            completedTrials++;

            if (onTrialComplete) {
              onTrialComplete(trialResult, completedTrials, totalTrials);
            }
          }

          if (completedTrials < totalTrials) {
            if (!abortSignal?.aborted) {
              setTimeout(runTrials, 0);
            } else {
              reject(new DOMException('Simulation aborted by user', 'AbortError'));
            }
          } else {
            console.log(`Simulation complete: ${completedTrials} trials`);
            resolve(allTrialResults);
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            resolve(allTrialResults); // Return partial results on abort
          } else {
            console.error('Error during simulation:', error);
            reject(error);
          }
        }
      };

      runTrials();
    });
  }

  /**
   * Run a single trial of the simulation
   */
  private runSingleTrial(capturedData: CapturedData, abortSignal?: AbortSignal): TrialResult {
    const {
      generalParams,
      peopleParams,
      peopleStates,
      trialCareerTimings,
      trialLifespans,
      initialSharedBalance,
      trialCostOfLiving,
      trialPerPersonNeedProportion
    } = this.initializeTrialState(capturedData);

    // Initialize inflation model for this trial
    const inflationParam = generalParams['inflation-rate'] as Parameter;
    const inflationModel = new InflationModel(
      inflationParam.getMean(),
      inflationParam.getStandardDeviation()
    );

    let sharedBalance = initialSharedBalance;
    const yearlyData: YearData[] = [];
    const simStartDate = new Date((generalParams['sim-start-date'] as any).value + 'T00:00:00');
    let cumulativeInflationFactor = 1.0;
    let firstRetirementYearIndex = -1;
    let cumulativeDeclineFactor = 1.0;
    let trialWasEverOverdrawn = false;

    // Main simulation loop
    for (let year = 0; year < SIMULATION_YEARS; year++) {
      if (abortSignal?.aborted) {
        throw new DOMException('Simulation aborted by user', 'AbortError');
      }

      const currentSimYear = simStartDate.getFullYear() + year;
      const yearlyInflationRate = inflationModel.getNextInflationRate() / 100;
      // Note: We'll apply this year's inflation at the END of the year loop
      // so that year 0 uses cumulativeInflationFactor = 1.0 (today's dollars)

      const yearlyNeedDeclineRate = ((generalParams['annual-need-decline'] as Parameter)?.get() ?? 0) / 100;

      let totalYearlyDeposits = 0;
      let totalYearlySSBenefit = 0;
      let totalYearlyNonRetiredIncome = 0;
      const personYearlyResults: PersonYearResult[] = [];

      // Process each person
      for (const personId in peopleStates) {
        const personInfo = capturedData.personal.people.find(p => p.id === personId)!;
        const trialPersonLifespan = trialLifespans[personId];

        if (peopleStates[personId].status === 'Deceased') {
          personYearlyResults.push({
            age: peopleStates[personId].currentAge + 1,
            name: personInfo.name,
            status: 'Deceased',
            totalIncome: 0,
            investmentDeposit: 0,
            socialSecurityBenefit: 0
          });
          peopleStates[personId].currentAge += 1;
          continue;
        }

        const personResult = this.simulatePersonYear(
          peopleStates[personId],
          peopleParams[personId],
          generalParams,
          year,
          trialCareerTimings[personId],
          cumulativeInflationFactor,
          personInfo.name,
          trialPersonLifespan,
          yearlyInflationRate
        );

        let personSSBenefit = 0;
        if (personResult.status === 'Retired') {
          const ssParam = peopleParams[personId].base.socialSecurityMonthlyBenefit;
          if (ssParam && 'value' in ssParam) {
            const baseAnnualSSBenefit = (ssParam.value as number) * 12;
            personSSBenefit = baseAnnualSSBenefit * cumulativeInflationFactor;
            totalYearlySSBenefit += personSSBenefit;
          }
        } else if (personResult.status !== 'Deceased') {
          totalYearlyNonRetiredIncome += personResult.totalIncome;
        }

        personYearlyResults.push({
          age: peopleStates[personId].currentAge,
          name: personInfo.name,
          status: personResult.status,
          totalIncome: personResult.totalIncome,
          investmentDeposit: personResult.totalDeposits,
          socialSecurityBenefit: personSSBenefit
        });

        // Update state for next year
        peopleStates[personId].currentAge += 1;
        peopleStates[personId].activeCareers = personResult.updatedActiveCareers;
        peopleStates[personId].status = personResult.status;

        if (personResult.status !== 'Deceased') {
          totalYearlyDeposits += personResult.totalDeposits;
        }
      }

      // Handle survivor benefits
      this.handleSurvivorBenefits(personYearlyResults, capturedData, peopleParams, cumulativeInflationFactor);
      totalYearlySSBenefit = personYearlyResults.reduce((sum, p) => sum + (p.socialSecurityBenefit || 0), 0);

      // Calculate investment rate
      const preRetRate = (generalParams['invest-pre-ret-apr'] as Parameter).get() / 100;
      const postRetRate = (generalParams['invest-post-ret-apr'] as Parameter).get() / 100;
      const numLiving = personYearlyResults.filter(p => p.status !== 'Deceased').length;
      const numRetired = personYearlyResults.filter(p => p.status === 'Retired').length;
      const proportionRetired = numLiving > 0 ? numRetired / numLiving : 0;
      const annualInvestmentRate = preRetRate + (postRetRate - preRetRate) * proportionRetired;

      // Check if all persons are deceased
      const allDeceased = personYearlyResults.every(p => p.status === 'Deceased');
      if (allDeceased) {
        break;
      }

      // Calculate retirement spending
      let neededIncome = 0;
      let requiredWithdrawal = 0;
      let isOverdrawn = false;
      const isAnyoneRetired = personYearlyResults.some(p => p.status === 'Retired');

      if (isAnyoneRetired && firstRetirementYearIndex === -1) {
        firstRetirementYearIndex = year;
      }

      if (isAnyoneRetired) {
        const baseAdjustedNeed = trialCostOfLiving * cumulativeInflationFactor;
        
        if (year > firstRetirementYearIndex) {
          cumulativeDeclineFactor *= Math.max(0, 1 - yearlyNeedDeclineRate);
        }
        
        // Apply per-person need reduction based on deceased family members
        const totalFamilyMembers = capturedData.personal.people.length;
        const livingFamilyMembers = personYearlyResults.filter(p => p.status !== 'Deceased').length;
        const deceasedMembers = totalFamilyMembers - livingFamilyMembers;
        
        let needAdjustmentFactor = 1.0;
        if (deceasedMembers > 0 && livingFamilyMembers > 0) {
          // Reduce need by per-person proportion for each deceased member
          // The last person always retains 100% of the original per-person need
          // So we calculate: remaining need = (100% - reductions) but ensure minimum of (100% / total family size)
          const reductionFactor = deceasedMembers * trialPerPersonNeedProportion;
          const minNeedFactor = 1.0 / totalFamilyMembers; // Each person's minimum individual share
          needAdjustmentFactor = Math.max(minNeedFactor, 1.0 - reductionFactor);
        }
        
        neededIncome = baseAdjustedNeed * cumulativeDeclineFactor * needAdjustmentFactor;
        requiredWithdrawal = Math.max(0, neededIncome - totalYearlySSBenefit - totalYearlyNonRetiredIncome);
      }

      // Update shared balance
      const startingBalanceYear = sharedBalance;
      const growthAmount = sharedBalance * annualInvestmentRate;
      sharedBalance += growthAmount + totalYearlyDeposits;

      if (requiredWithdrawal > 0) {
        sharedBalance -= requiredWithdrawal;
        if (sharedBalance < 0) {
          isOverdrawn = true;
          trialWasEverOverdrawn = true;
        }
      }

      // Record yearly data
      yearlyData.push({
        year: currentSimYear,
        inflationRate: yearlyInflationRate,
        persons: personYearlyResults,
        sharedAccount: {
          startingBalance: startingBalanceYear,
          rateOfReturn: annualInvestmentRate,
          totalDeposit: totalYearlyDeposits,
          neededIncome,
          totalSSBenefit: totalYearlySSBenefit,
          totalNonRetiredIncome: totalYearlyNonRetiredIncome,
          withdrawal: requiredWithdrawal,
          endingBalance: sharedBalance,
          isOverdrawn
        }
      });

      // Apply this year's inflation for use in next year
      // This ensures year 0 calculations use factor 1.0 (today's dollars),
      // and year 1 uses year 0's inflation, etc.
      cumulativeInflationFactor *= (1 + yearlyInflationRate);
    }

    return {
      finalBalance: sharedBalance,
      history: yearlyData,
      wasOverdrawn: trialWasEverOverdrawn
    };
  }

  /**
   * Simulate a single person for one year
   */
  private simulatePersonYear(
    personState: PersonState,
    personParams: InstantiatedPersonParams,
    generalParams: InstantiatedGeneralParams,
    yearIndex: number,
    trialCareerTimings: Record<string, { startAge: number; endAge: number }>,
    cumulativeInflationFactor: number,
    personName: string,
    trialPersonLifespan: number,
    yearlyInflationRate: number
  ): {
    totalDeposits: number;
    totalIncome: number;
    status: 'Working' | 'Retired' | 'Deceased';
    updatedActiveCareers: ActiveCareer[];
  } {
    const currentAge = personState.currentAge;
    const retirementAge = (personParams.base.retirementAge as any).value;

    let totalWageInvestment = 0;
    let totalOtherInvestment = 0;
    let totalYearlyIncome = 0;
    const updatedActiveCareers: ActiveCareer[] = [];
    let status: 'Working' | 'Retired' | 'Deceased';

    if (currentAge >= trialPersonLifespan) {
      status = 'Deceased';
    } else if (currentAge >= retirementAge) {
      status = 'Retired';
    } else {
      status = 'Working';
    }

    if (status !== 'Deceased') {
      personParams.careers.forEach(careerParams => {
        const careerId = careerParams.id;
        const trialTimings = trialCareerTimings[careerId];

        const isActive = (status === 'Working') && 
          (currentAge >= trialTimings.startAge && currentAge < trialTimings.endAge);

        if (isActive) {
          let currentCareerState = personState.activeCareers.find(c => c.id === careerId);

          if (!currentCareerState) {
            const initialIncome = careerParams.initialIncome.get();
            const currentWage = initialIncome * cumulativeInflationFactor;
            
            currentCareerState = { id: careerId, currentWage };
            totalYearlyIncome += currentWage;
          } else {
            const incomeIncreasePercent = careerParams.annualIncomeIncrease.get() / 100;
            
            // Apply inflation adjustment if enabled
            let totalIncreaseRate = incomeIncreasePercent;
            if (careerParams.inflationAdjustedIncrease) {
              // Add inflation rate (clamped to be no lower than zero)
              totalIncreaseRate += Math.max(0, yearlyInflationRate);
            }
            
            currentCareerState.currentWage *= (1 + totalIncreaseRate);
            totalYearlyIncome += currentCareerState.currentWage;
          }

          const investmentPercent = careerParams.incomeInvestment.get() / 100;
          const wageInvestmentAmount = currentCareerState.currentWage * investmentPercent;
          totalWageInvestment += wageInvestmentAmount;

          const employerMatchPercent = careerParams.employerMatch.get() / 100;
          const employerMatchAmount = currentCareerState.currentWage * employerMatchPercent;
          totalWageInvestment += employerMatchAmount;

          const otherInvestmentAmount = careerParams.otherInvestment.get();
          const inflatedOtherInvestment = otherInvestmentAmount * cumulativeInflationFactor;
          totalOtherInvestment += inflatedOtherInvestment;

          updatedActiveCareers.push(currentCareerState);
        }
      });
    }

    const totalDeposits = totalWageInvestment + totalOtherInvestment;

    return {
      totalDeposits,
      totalIncome: totalYearlyIncome,
      status,
      updatedActiveCareers
    };
  }

  /**
   * Initialize state for a single trial
   */
  private initializeTrialState(capturedData: CapturedData) {
    // Instantiate general parameters
    const generalParams: InstantiatedGeneralParams = {};
    for (const key in capturedData.general) {
      const param = capturedData.general[key];
      if (param.type === 'date') {
        generalParams[key] = { value: param.value as string };
      } else if (param.type === 'meanStdDev') {
        const meanStdParam = param as any;
        generalParams[key] = new Parameter(meanStdParam.mean, meanStdParam.stdDev);
      }
    }

    // Instantiate people parameters
    const peopleParams: Record<string, InstantiatedPersonParams> = {};
    capturedData.personal.people.forEach(personInfo => {
      const personId = personInfo.id;
      const baseParams = capturedData.personal.parameterValues[personId].parameterValues;
      const careerData = capturedData.personal.parameterValues[personId].careers;

      peopleParams[personId] = { base: {}, careers: [] };

      // Process base parameters
      for (const key in baseParams) {
        const param = baseParams[key];
        if (param && typeof param === 'object') {
          if (param.type === 'date' || param.type === 'number') {
            peopleParams[personId].base[key] = { value: param.value };
          } else if (param.type === 'meanStdDev') {
            const meanStdParam = param as any;
            peopleParams[personId].base[key] = new Parameter(meanStdParam.mean, meanStdParam.stdDev);
          }
        }
      }

      // Process career parameters
      careerData.forEach(career => {
        const careerParamsInst: InstantiatedCareerParams = {
          id: career.id,
          startAge: new Parameter(career.startAge.mean, career.startAge.stdDev),
          isStartNow: career.isStartNow,
          endAge: new Parameter(career.endAge.mean, career.endAge.stdDev),
          isEndAtRetirement: career.isEndAtRetirement,
          initialIncome: new Parameter(career.initialIncome.mean, career.initialIncome.stdDev),
          annualIncomeIncrease: new Parameter(career.annualIncomeIncrease.mean, career.annualIncomeIncrease.stdDev),
          incomeInvestment: new Parameter(career.incomeInvestment.mean, career.incomeInvestment.stdDev),
          employerMatch: new Parameter(career.employerMatch.mean, career.employerMatch.stdDev),
          otherInvestment: new Parameter(career.otherInvestment.mean, career.otherInvestment.stdDev),
          inflationAdjustedIncrease: career.inflationAdjustedIncrease
        };
        peopleParams[personId].careers.push(careerParamsInst);
      });
    });

    // Sample trial-specific parameters
    const trialCostOfLiving = (generalParams['cost-of-living-retirement'] as Parameter)?.get() ?? 60000;
    const trialCareerTimings: Record<string, Record<string, { startAge: number; endAge: number }>> = {};
    const trialLifespans: Record<string, number> = {};
    const simStartDate = (generalParams['sim-start-date'] as any).value;

    capturedData.personal.people.forEach(personInfo => {
      const personId = personInfo.id;
      trialCareerTimings[personId] = {};

      const personBaseParams = peopleParams[personId].base;
      
      // Validate required parameters exist
      if (!personBaseParams.lifeExpectancy) {
        throw new Error(`Missing lifeExpectancy parameter for person ${personId}`);
      }
      if (!personBaseParams.birthdate) {
        throw new Error(`Missing birthdate parameter for person ${personId}`);
      }
      if (!personBaseParams.retirementAge) {
        throw new Error(`Missing retirementAge parameter for person ${personId}`);
      }
      
      const sampledLifespan = Math.round((personBaseParams.lifeExpectancy as Parameter).get());
      trialLifespans[personId] = sampledLifespan;

      const birthdate = (personBaseParams.birthdate as any).value;
      const retirementAge = (personBaseParams.retirementAge as any).value;
      const currentAgeAtSimStart = this.calculateCurrentAge(birthdate, simStartDate);

      const personCareerParams = peopleParams[personId].careers;
      personCareerParams.forEach(career => {
        let trialStartAge, trialEndAge;
        if (career.isStartNow) {
          trialStartAge = currentAgeAtSimStart;
        } else {
          trialStartAge = Math.round(career.startAge.get());
        }
        if (career.isEndAtRetirement) {
          trialEndAge = retirementAge;
        } else {
          trialEndAge = Math.round(career.endAge.get());
        }
        trialEndAge = Math.max(trialStartAge, trialEndAge);
        trialCareerTimings[personId][career.id] = { startAge: trialStartAge, endAge: trialEndAge };
      });
    });

    // Initialize person states
    const peopleStates: Record<string, PersonState> = {};
    const initialSharedBalance = (generalParams['initial-balance'] as Parameter)?.get() ?? 0;

    capturedData.personal.people.forEach(personInfo => {
      const personId = personInfo.id;
      const birthdate = (peopleParams[personId].base.birthdate as any).value;
      const initialAge = this.calculateCurrentAge(birthdate, simStartDate);
      const retirementAge = (peopleParams[personId].base.retirementAge as any).value;
      
      peopleStates[personId] = {
        id: personId,
        currentAge: initialAge,
        activeCareers: [],
        status: initialAge >= retirementAge ? 'Retired' : 'Working'
      };
    });

    // Sample per-person need proportion once for this trial
    const trialPerPersonNeedProportion = ((generalParams['per-person-need-proportion'] as Parameter)?.get() ?? 35.0) / 100;

    return {
      generalParams,
      peopleParams,
      peopleStates,
      trialCareerTimings,
      trialLifespans,
      initialSharedBalance,
      trialCostOfLiving,
      trialPerPersonNeedProportion
    };
  }

  /**
   * Handle survivor benefits calculation
   */
  private handleSurvivorBenefits(
    personYearlyResults: PersonYearResult[],
    capturedData: CapturedData,
    peopleParams: Record<string, InstantiatedPersonParams>,
    cumulativeInflationFactor: number
  ): void {
    if (personYearlyResults.length === 2) {
      const retiredPersonResult = personYearlyResults.find(p => p.status === 'Retired');
      const deceasedPersonResult = personYearlyResults.find(p => p.status === 'Deceased');

      if (retiredPersonResult && deceasedPersonResult) {
        const retiredPersonInfo = capturedData.personal.people.find(p => p.name === retiredPersonResult.name);
        const deceasedPersonInfo = capturedData.personal.people.find(p => p.name === deceasedPersonResult.name);

        if (retiredPersonInfo && deceasedPersonInfo) {
          const retiredId = retiredPersonInfo.id;
          const deceasedId = deceasedPersonInfo.id;

          const retiredSSParam = peopleParams[retiredId]?.base.socialSecurityMonthlyBenefit;
          const deceasedSSParam = peopleParams[deceasedId]?.base.socialSecurityMonthlyBenefit;

          if (retiredSSParam && 'value' in retiredSSParam && deceasedSSParam && 'value' in deceasedSSParam) {
            const retiredBaseAnnual = (retiredSSParam.value as number) * 12;
            const deceasedBaseAnnual = (deceasedSSParam.value as number) * 12;

            const retiredPotentialBenefit = retiredBaseAnnual * cumulativeInflationFactor;
            const deceasedPotentialBenefit = deceasedBaseAnnual * cumulativeInflationFactor;

            const maxBenefit = Math.max(retiredPotentialBenefit, deceasedPotentialBenefit);
            retiredPersonResult.socialSecurityBenefit = maxBenefit;
          }
        }
      }
    }
  }

  /**
   * Calculate current age from birthdate and simulation start date
   */
  private calculateCurrentAge(birthdateString: string, simStartDateString: string): number {
    const birthDate = new Date(birthdateString + 'T00:00:00');
    const simStartDate = new Date(simStartDateString + 'T00:00:00');
    
    if (isNaN(birthDate.getTime()) || isNaN(simStartDate.getTime())) {
      console.warn(`Invalid date format for age calculation: birthdate=${birthdateString}, simStart=${simStartDateString}`);
      return 0;
    }
    
    let age = simStartDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = simStartDate.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && simStartDate.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return Math.max(0, age);
  }

  /**
   * Validate captured data structure
   */
  private validateCapturedData(capturedData: CapturedData): boolean {
    if (!capturedData || !capturedData.general || !capturedData.personal) {
      return false;
    }
    
    if (!capturedData.personal.people || capturedData.personal.people.length === 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Abort current simulation
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }
}
